import { Button, ChevronLeft, ChevronRight } from "@app/ui";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { addDays } from "@app/core";
import { AnimatePresence } from "../../lib/motion/index.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import type { I18nKey } from "../../lib/language.js";
import { useSettingsStore } from "../../stores/index.js";
import { CollapsedBrand } from "../sidebar/SidebarChrome.js";
import { DayCountControl } from "./DayCountControl.js";
import { shiftAnchor } from "./shift-anchor.js";

interface Props {
  anchor: Date;
  onChange: (d: Date) => void;
  onToday?: () => void;
  unit: "month" | "week" | "days" | "day";
  /** Extra controls rendered to the right of the view switcher. */
  actions?: ReactNode;
}

export function CalendarHeader({ anchor, onChange, onToday, unit, actions }: Props) {
  const { t } = useI18n();
  const dayCount = useSettingsStore((s) => s.calendarDayCount);
  const sidebarOpen = useSettingsStore((s) => s.sidebarOpen);
  const showSwipeHint = useSettingsStore((s) => s.showCalendarSwipeHint);
  const mobile = useMobileUx();
  const locale = useAppLocale();
  function shift(delta: number) {
    onChange(shiftAnchor(anchor, unit, delta, dayCount));
  }
  // Mobile: ONE compact row — just the view switcher + Today. No prev/next
  // (swipe the grid instead), no title/date text, no brand, no inbox toggle
  // (right-edge swipe opens the inbox).
  if (mobile) {
    // Tighter paddings/gaps than desktop so the whole row (view switcher + Today)
    // fits a phone width without clipping the Today button off the right edge.
    return (
      <div className="flex flex-col">
        {showSwipeHint && (
          <div className="flex items-center justify-between px-4 pb-0.5 pt-1.5 text-xs italic text-muted-foreground">
            <span>{t("calendar.swipeFilters")}</span>
            <span>{t("calendar.slideToExpand")}</span>
            <span>{t("calendar.swipeInbox")}</span>
          </div>
        )}
        <div className={`no-scrollbar flex flex-nowrap items-center justify-between gap-1 overflow-x-auto px-2 pb-1.5 min-[341px]:gap-1.5 min-[341px]:px-4 ${showSwipeHint ? "pt-0.5" : "pt-2.5"}`}>
          <nav className="flex shrink-0 flex-nowrap items-center gap-0.5 rounded-md border bg-card p-0.5 text-xs min-[341px]:text-sm">
            <Link to="/calendar/month" className={tab(unit === "month", true)}>{t("common.month")}</Link>
            <Link to="/calendar/week" className={tab(unit === "week", true)}>{t("common.week")}</Link>
            <DayCountControl active={unit === "days"} compact />
            <Link to="/calendar/day" className={tab(unit === "day", true)}>{t("common.day")}</Link>
          </nav>
          <TodayButton label={t("common.today")} onClick={onToday ?? (() => onChange(new Date()))} compact />
        </div>
      </div>
    );
  }
  const fmt = formatTitle(anchor, unit, dayCount, locale);
  return (
    <div className="no-scrollbar flex flex-nowrap items-center justify-between gap-2 overflow-x-auto">
      <div className="flex min-w-0 items-center gap-2">
        {/* While the whole sidebar is collapsed, the app brand joins this top bar. */}
        <AnimatePresence initial={false}>
          {!sidebarOpen && <CollapsedBrand key="brand" className="mr-2 h-9" />}
        </AnimatePresence>
        <Button variant="outline" size="icon" aria-label={t("calendar.previousUnit", { unit: t(unitLabelKey(unit)) })} onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" size="icon" aria-label={t("calendar.nextUnit", { unit: t(unitLabelKey(unit)) })} onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="outline" size="sm" onClick={onToday ?? (() => onChange(new Date()))}>{t("common.today")}</Button>
        <h1 className="ml-3 min-w-0 truncate text-lg font-semibold sm:text-xl">{fmt}</h1>
      </div>
      <div className="flex shrink-0 flex-nowrap items-center gap-2">
        <nav className="flex shrink-0 flex-nowrap items-center gap-1 rounded-md border bg-card p-1 text-sm">
          <Link to="/calendar/month" className={tab(unit === "month")}>{t("common.month")}</Link>
          <Link to="/calendar/week" className={tab(unit === "week")}>{t("common.week")}</Link>
          <DayCountControl active={unit === "days"} />
          <Link to="/calendar/day" className={tab(unit === "day")}>{t("common.day")}</Link>
        </nav>
        {actions}
      </div>
    </div>
  );
}

function formatTitle(anchor: Date, unit: Props["unit"], dayCount: number, locale: string): string {
  if (unit === "day") {
    return anchor.toLocaleDateString(locale, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  }
  if (unit === "days") {
    const end = addDays(anchor, Math.max(0, dayCount - 1));
    const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
    return `${anchor.toLocaleDateString(locale, opts)} – ${end.toLocaleDateString(locale, { ...opts, year: "numeric" })}`;
  }
  return anchor.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

function tab(active: boolean, compact = false): string {
  return `whitespace-nowrap rounded ${compact ? "px-1.5 min-[341px]:px-2" : "px-3"} py-1 ${active ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`;
}

function unitLabelKey(unit: Props["unit"]): I18nKey {
  if (unit === "month") return "common.month";
  if (unit === "week") return "common.week";
  return "common.day";
}

// Mirrors the view-selector <nav> box exactly (same border/bg/padding) so Today
// matches its design and height. `compact` tightens it for the phone header.
function TodayButton({ label, onClick, compact = false }: { label: string; onClick: () => void; compact?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={`shrink-0 rounded-md border bg-card text-sm ${compact ? "p-0.5" : "p-1"}`}>
      <span className={`block whitespace-nowrap rounded py-1 hover:bg-accent ${compact ? "px-1.5 text-xs min-[341px]:px-2 min-[341px]:text-sm" : "px-3"}`}>{label}</span>
    </button>
  );
}
